import { z } from 'zod';
import {
    ReportChromeDocumentContextSchema,
    ReportChromeHistoricalLayoutRefSchema,
    ReportChromeLayoutRefSchema,
} from './report-chrome.js';

export const REPORT_BRANDING_SCHEMA_VERSION = 2;
export const REPORT_BRANDING_FONT_FAMILIES = Object.freeze([
    'Inter',
    'Noto Sans',
    'Roboto',
    'Source Sans 3',
    'IBM Plex Sans',
    'DejaVu Sans',
    'Noto Serif',
    'Source Serif 4',
    'IBM Plex Serif',
]);
export const REPORT_BRANDING_LEGACY_FONT_FAMILIES = Object.freeze([
    ...REPORT_BRANDING_FONT_FAMILIES,
    'Arial',
]);
export const REPORT_BRANDING_DATE_ORDERS = Object.freeze(['dmy', 'mdy']);
export const REPORT_BRANDING_WATERMARK_SOURCES = Object.freeze(['text', 'classification']);

const id = z.string().trim().min(1).max(120);
const operationId = z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$/);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const color = z.string().regex(/^#[0-9a-fA-F]{6}$/).transform((value) => value.toLowerCase());

export const ReportBrandingLogoSchema = z.object({
    resourceId: id,
    contentType: z.enum(['image/png', 'image/jpeg']),
    size: z.number().int().positive().max(2_097_152),
    width: z.number().int().positive().max(2000),
    height: z.number().int().positive().max(2000),
    sha256,
}).strict();

export const ReportBrandingPaletteSchema = z.object({
    primary: color,
    accent: color,
    text: color,
    mutedText: color,
    background: color,
    surface: color,
    border: color,
}).strict();

export const ReportBrandingTypographySchema = z.object({
    headingFamily: z.enum(REPORT_BRANDING_LEGACY_FONT_FAMILIES),
    bodyFamily: z.enum(REPORT_BRANDING_LEGACY_FONT_FAMILIES),
    baseSizePt: z.number().min(7).max(16),
    scale: z.number().min(1).max(2),
}).strict();

export const ReportBrandingChromeSchema = z.object({
    dateOrder: z.enum(REPORT_BRANDING_DATE_ORDERS),
    layout: ReportChromeLayoutRefSchema.nullable(),
}).strict();

const ReportBrandingChromeWriteSchema = z.object({
    dateOrder: z.enum(REPORT_BRANDING_DATE_ORDERS),
}).strict();

function watermarkSources(value) {
    return Array.isArray(value.sources)
        ? value.sources
        : [value.type === 'classification' ? 'classification' : 'text'];
}

function validateWatermarkPolicy(value, ctx) {
    const sources = watermarkSources(value);
    if (new Set(sources).size !== sources.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sources'],
            message: 'Watermark sources must be unique',
        });
    }
    if (!value.enabled) return;
    if (!sources.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sources'],
            message: 'At least one watermark source is required when the watermark is enabled',
        });
        return;
    }
    if (sources.includes('text') && !value.text.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['text'],
            message: 'Watermark text is required when the watermark includes custom text',
        });
    }
    if (sources.includes('classification') && !value.classificationId?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['classificationId'],
            message: 'A classification is required when the watermark includes a classification',
        });
    }
}

export const ReportWatermarkPolicySchema = z.object({
    enabled: z.boolean(),
    type: z.enum(['text', 'classification']).optional(),
    sources: z.array(z.enum(REPORT_BRANDING_WATERMARK_SOURCES)).max(2).optional(),
    text: z.string().trim().max(160),
    classificationId: z.string().trim().max(120).optional(),
    classificationText: z.string().trim().max(160).optional(),
}).strict().superRefine(validateWatermarkPolicy).transform((value) => {
    const sources = watermarkSources(value);
    return {
        ...value,
        type: sources[0] || value.type || 'text',
        ...(Array.isArray(value.sources) ? { sources } : {}),
    };
});

export const ReportBrandingSnapshotSchema = z.object({
    schemaVersion: z.literal(REPORT_BRANDING_SCHEMA_VERSION),
    logo: ReportBrandingLogoSchema.nullable(),
    watermark: ReportWatermarkPolicySchema.optional(),
    palette: ReportBrandingPaletteSchema,
    typography: ReportBrandingTypographySchema,
    chrome: ReportBrandingChromeSchema,
}).strict();

export const DEFAULT_REPORT_BRANDING_SNAPSHOT = Object.freeze({
    schemaVersion: REPORT_BRANDING_SCHEMA_VERSION,
    logo: null,
    watermark: Object.freeze({ enabled: false, type: 'text', sources: Object.freeze(['text']), text: '' }),
    palette: Object.freeze({
        primary: '#1f4e78',
        accent: '#2f75b5',
        text: '#1f2937',
        mutedText: '#6b7280',
        background: '#ffffff',
        surface: '#f8fafc',
        border: '#d1d5db',
    }),
    typography: Object.freeze({
        headingFamily: 'DejaVu Sans',
        bodyFamily: 'DejaVu Sans',
        baseSizePt: 10,
        scale: 1.2,
    }),
    chrome: Object.freeze({
        dateOrder: 'dmy',
        layout: null,
    }),
});

export const ReportBrandingWriteSchema = ReportBrandingSnapshotSchema.omit({ logo: true, typography: true, chrome: true }).extend({
    typography: z.object({
        headingFamily: z.enum(REPORT_BRANDING_FONT_FAMILIES),
        bodyFamily: z.enum(REPORT_BRANDING_FONT_FAMILIES),
        baseSizePt: z.number().min(7).max(16),
        scale: z.number().min(1).max(2),
    }).strict(),
    chrome: ReportBrandingChromeWriteSchema,
    expectedVersion: z.number().int().nonnegative(),
}).strict();

export const ReportBrandingLogoWriteSchema = z.object({
    expectedVersion: z.coerce.number().int().nonnegative(),
}).strict();

export const ReportBrandingLogoDeleteSchema = z.object({
    expectedVersion: z.coerce.number().int().nonnegative(),
}).strict();

export const ReportBrandingSchema = ReportBrandingSnapshotSchema.extend({
    tenant: id,
    version: z.number().int().positive(),
    revisionId: id,
    updatedBy: id,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
}).strict();

const legacyColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const historicalPalette = z.object({
    primary: legacyColor,
    accent: legacyColor,
    text: legacyColor,
    mutedText: legacyColor,
    background: legacyColor,
    surface: legacyColor,
    border: legacyColor,
}).strict();
const historicalTypography = z.object({
    headingFamily: z.enum(REPORT_BRANDING_LEGACY_FONT_FAMILIES),
    bodyFamily: z.enum(REPORT_BRANDING_LEGACY_FONT_FAMILIES),
    baseSizePt: z.number().min(7).max(16),
    scale: z.number().min(1).max(2),
}).strict();
const ReportBrandingHistoricalWatermarkSchema = z.object({
    enabled: z.boolean(),
    type: z.enum(['text', 'classification']).optional(),
    sources: z.array(z.enum(REPORT_BRANDING_WATERMARK_SOURCES)).max(2).optional(),
    text: z.string().max(160),
    classificationId: z.string().max(120).optional(),
    classificationText: z.string().max(160).optional(),
}).strict();
const ReportBrandingLegacySnapshotSchema = z.object({
    schemaVersion: z.literal(1),
    logo: ReportBrandingLogoSchema.nullable().optional(),
    watermark: ReportBrandingHistoricalWatermarkSchema.optional(),
    palette: historicalPalette,
    typography: historicalTypography,
    chrome: z.object({
        dateOrder: z.enum(REPORT_BRANDING_DATE_ORDERS).optional(),
        headerText: z.string().max(500).optional(),
        footerText: z.string().max(500).optional(),
        showGeneratedDate: z.boolean().optional(),
        showPageNumbers: z.boolean().optional(),
        showPreparedBy: z.boolean().optional(),
    }).strict(),
}).strict();

export { ReportBrandingLegacySnapshotSchema };

export const ReportBrandingV2HistoricalSnapshotSchema = z.object({
    schemaVersion: z.literal(REPORT_BRANDING_SCHEMA_VERSION),
    logo: ReportBrandingLogoSchema.nullable(),
    watermark: ReportBrandingHistoricalWatermarkSchema.optional(),
    palette: historicalPalette,
    typography: historicalTypography,
    chrome: z.object({
        dateOrder: z.enum(REPORT_BRANDING_DATE_ORDERS),
        layout: ReportChromeHistoricalLayoutRefSchema.nullable(),
    }).strict(),
}).strict();

export const ReportBrandingHistoricalSnapshotSchema = z.union([
    ReportBrandingLegacySnapshotSchema,
    ReportBrandingV2HistoricalSnapshotSchema,
]);

export const ReportBrandingRevisionSchema = z.object({
    id,
    tenant: id,
    version: z.number().int().positive(),
    operationId,
    snapshot: ReportBrandingHistoricalSnapshotSchema,
    digest: sha256,
    updatedBy: id,
    createdAt: z.coerce.date(),
    operationKind: z.string().trim().min(1).max(64).optional(),
    operationFingerprint: sha256.optional(),
}).strict();

export const ResolvedReportBrandingSchema = z.object({
    version: z.number().int().positive(),
    revisionId: id,
    snapshot: ReportBrandingSnapshotSchema,
    digest: sha256,
}).strict();

const runtimeLogoBase64 = z.string().min(4).max(2_796_204).regex(
    /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/,
);

export const ReportChromeRuntimeBrandingSchema = ResolvedReportBrandingSchema.extend({
    snapshot: ReportBrandingSnapshotSchema.extend({
        logo: ReportBrandingLogoSchema.extend({
            dataBase64: runtimeLogoBase64,
        }).nullable(),
    }).strict(),
}).strict();

export const ReportChromeDocumentContextResponseSchema = z.object({
    context: ReportChromeDocumentContextSchema,
    branding: ReportChromeRuntimeBrandingSchema,
}).strict();
